import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  disconnectCalendar,
  fetchIntegrations,
  startCalendarLink,
  testConnection,
  type IntegrationProvider,
} from '@/lib/integrations.ts';

/** A connected calendar, by the account row that disconnecting unlinks. */
export interface CalendarAccount {
  provider: IntegrationProvider;
  accountId: string;
}

/* Null while untested — "we have not asked" is not the same as "it failed". */
export type TestResult = boolean | null;

export interface CalendarConnections {
  isPending: boolean;
  isError: boolean;
  /** The connections have been read, so there are rows to show. */
  loaded: boolean;
  /** The account row holding a provider's grant, or null while not connected. */
  accountIdOf: (provider: IntegrationProvider) => string | null;
  /** This provider's test is running. */
  testing: (provider: IntegrationProvider) => boolean;
  resultOf: (provider: IntegrationProvider) => TestResult;
  /** A connect or disconnect is under way. */
  busy: boolean;
  /** A test is running for some calendar. */
  testRunning: boolean;
  /** The last connect or disconnect failed. */
  failed: boolean;
  connect: (provider: IntegrationProvider) => void;
  disconnect: (account: CalendarAccount) => void;
  test: (provider: IntegrationProvider) => void;
}

/**
 * The calendars this person connected, and the three things they can do with
 * one: connect it, test it, disconnect it. A test's answer is kept per
 * provider until the calendar is disconnected.
 */
export function useCalendarConnections(enabled: boolean): CalendarConnections {
  const queryClient = useQueryClient();
  const [tested, setTested] = useState<Partial<Record<IntegrationProvider, TestResult>>>(
    {},
  );

  const integrations = useQuery({
    queryKey: ['integrations', 'status'],
    queryFn: fetchIntegrations,
    enabled,
  });

  /*
   * Connecting leaves the app for the provider's consent screen, so it stays
   * pending until the browser goes. A granted calendar lands on the agenda, what
   * someone connected it to see; a refusal comes back here, to the buttons that
   * try again.
   */
  const link = useMutation({
    mutationFn: (provider: IntegrationProvider) =>
      startCalendarLink(provider, `${window.location.origin}/`),
  });

  const unlink = useMutation({
    mutationFn: ({ accountId }: CalendarAccount) => disconnectCalendar(accountId),
    onSuccess: (_result, { provider }) => {
      setTested((results) => ({ ...results, [provider]: null }));
      return queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });

  const check = useMutation({
    mutationFn: testConnection,
    onSuccess: (ok, provider) => setTested((results) => ({ ...results, [provider]: ok })),
    /* A failed request is a failed test — the calendar is unreachable either way. */
    onError: (_error, provider) =>
      setTested((results) => ({ ...results, [provider]: false })),
  });

  return {
    isPending: integrations.isPending,
    isError: integrations.isError,
    loaded: integrations.data !== undefined,
    /* Disconnecting unlinks the account row that holds the grant, so it is kept per row. */
    accountIdOf: (provider) =>
      integrations.data?.find((entry) => entry.provider === provider)?.account_id ?? null,
    testing: (provider) => check.isPending && check.variables === provider,
    resultOf: (provider) => tested[provider] ?? null,
    busy: link.isPending || unlink.isPending,
    testRunning: check.isPending,
    failed: link.isError || unlink.isError,
    connect: (provider) => link.mutate(provider),
    disconnect: (account) => unlink.mutate(account),
    test: (provider) => check.mutate(provider),
  };
}
